import { memo, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { cn } from "@/app/utils/cn";
import { trackProductClick } from "@/app/lib/tracking/interactions";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/shadcn/tooltip";

interface ProductColorsProps {
  selectedColorIndex: number;
  loadingColorIndex: number | null;
  onColorChange: (colorIndex: number, slug: string) => void;
}

const ProductColors = memo(
  ({
    selectedColorIndex,
    loadingColorIndex,
    onColorChange,
  }: ProductColorsProps) => {
    const { product } = useProductDetail();
    const colorsSwiperRef = useRef<SwiperType | null>(null);
    const [showLeftGradient, setShowLeftGradient] = useState(false);
    const [showRightGradient, setShowRightGradient] = useState(false);
    const [showNavigation, setShowNavigation] = useState(false);

    const updateGradients = useCallback((swiper: SwiperType): void => {
      const isBeginning = swiper.isBeginning;
      const isEnd = swiper.isEnd;
      const hasScroll = !isBeginning || !isEnd;
      setShowLeftGradient(!isBeginning);
      setShowRightGradient(!isEnd);
      setShowNavigation(hasScroll);
    }, []);

    const handleColorsPrev = useCallback(() => {
      colorsSwiperRef.current?.slidePrev();
    }, []);

    const handleColorsNext = useCallback(() => {
      colorsSwiperRef.current?.slideNext();
    }, []);

    const handleSwiperInit = useCallback(
      (swiper: SwiperType) => {
        colorsSwiperRef.current = swiper;
        updateGradients(swiper);
      },
      [updateGradients],
    );

    const handleColorClick = useCallback(
      (index: number, slug: string) => {
        if (loadingColorIndex !== null || index === selectedColorIndex) return;
        if (product?.id) trackProductClick(product.id);
        onColorChange(index, slug);
      },
      [onColorChange, loadingColorIndex, selectedColorIndex, product?.id],
    );

    if (!product?.variants_by_color?.length) return null;

    const colors = product.variants_by_color;
    const selectedColor = colors[selectedColorIndex];

    return (
      <>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 text-sm">
            <div className="flex items-center gap-1">
              <span className="font-medium">Renk: </span>
              <p className="uppercase">{selectedColor?.color_name || ""}</p>
            </div>
            {selectedColor?.info && (
              <Tooltip>
                <TooltipTrigger>
                  <Info
                    size={12}
                    className="text-black cursor-pointer transition-all duration-300 ease-in-out hover:text-main"
                  />
                </TooltipTrigger>
                <TooltipContent
                  className="w-[320px] sm:w-122.5"
                  variant="white"
                >
                  <p className="text-xs">{selectedColor.info}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {showNavigation && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleColorsPrev}
                disabled={!showLeftGradient}
                className={cn(
                  "cursor-pointer text-muted-foreground transition-all duration-300 ease-in-out hover:text-main",
                  !showLeftGradient && "opacity-30 cursor-not-allowed",
                )}
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                onClick={handleColorsNext}
                disabled={!showRightGradient}
                className={cn(
                  "cursor-pointer text-muted-foreground transition-all duration-300 ease-in-out hover:text-main",
                  !showRightGradient && "opacity-30 cursor-not-allowed",
                )}
              >
                <ChevronRight />
              </button>
            </div>
          )}
        </div>
        {/* Pre-init genişlikler globals.css'te (.colors-swiper:not(.swiper-initialized)) —
            skeleton swap yok, SSR HTML'de gerçek slide'lar doğru genişlikte görünür. */}
        <Swiper
          className="colors-swiper py-3! px-1!"
          spaceBetween={8}
          slidesPerView={5}
          slidesPerGroup={4}
          onSwiper={handleSwiperInit}
          onSlideChange={updateGradients}
          breakpoints={{
            375: { slidesPerView: 5.5, slidesPerGroup: 5, spaceBetween: 8 },
            480: { slidesPerView: 6.5, slidesPerGroup: 6, spaceBetween: 10 },
            640: { slidesPerView: 7.5, slidesPerGroup: 7, spaceBetween: 10 },
            768: { slidesPerView: 8.5, slidesPerGroup: 8, spaceBetween: 10 },
            1024: { slidesPerView: 8.2, slidesPerGroup: 8, spaceBetween: 12 },
            1200: { slidesPerView: 9.8, slidesPerGroup: 9, spaceBetween: 12 },
          }}
        >
          {colors.map((color, i) => {
            const imageUrl =
              color.primary_image || color.images?.[0] || product.images?.[0];
            const isLoading = loadingColorIndex === i;
            const isSelected = i === selectedColorIndex;
            const isOutOfStock = !color.stock || color.stock === 0;

            return (
              <SwiperSlide key={color.variant_id}>
                <button
                  type="button"
                  onClick={() => handleColorClick(i, color.slug)}
                  disabled={loadingColorIndex !== null || isSelected || isOutOfStock}
                  className="flex flex-col items-center gap-1 w-full cursor-pointer disabled:cursor-not-allowed"
                >
                  <div className={cn(
                    "relative w-full aspect-[2/3] rounded-[6px] border border-gray-200 overflow-hidden bg-white hover:ring-2 hover:ring-main transition-colors",
                    {
                      "ring-2 ring-main": isSelected,
                      "opacity-50": isLoading,
                      "opacity-60": isOutOfStock && !isLoading,
                    },
                  )}>
                    <FallbackImage
                      src={imageUrl || ""}
                      alt={color.color_name}
                      fill
                      className="object-cover"
                      sizes="80px"
                      quality={70}
                      loading="lazy"
                    />
                    {isOutOfStock && !isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <svg
                          className="absolute inset-0 w-full h-full"
                          viewBox="0 0 100 150"
                          preserveAspectRatio="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <line
                            x1="5" y1="5" x2="95" y2="145"
                            stroke="rgba(150,150,150,0.7)"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    )}
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-[6px]">
                        <svg
                          className="animate-spin h-6 w-6 text-main"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    )}
                    {color.is_popular && (
                      <div className="absolute rounded-sm leading-3.5 px-1 -top-2.5 z-50 border border-[#fcd8bb] left-1/2 -translate-x-1/2 text-[10px] text-main bg-[#fef4eb]">
                        Populer
                      </div>
                    )}
                  </div>
                  <span className={cn(
                    "text-[9px] leading-tight text-center w-full truncate",
                    isSelected ? "text-main font-medium" : "text-gray-500"
                  )}>
                    {color.color_name}
                  </span>
                </button>
              </SwiperSlide>
            );
          })}
          {showNavigation && (
            <>
              <div
                className={`absolute top-0 left-0 z-20 w-5 sm:w-8 lg:w-13.75 h-full bg-linear-to-r from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${
                  showLeftGradient ? "opacity-100" : "opacity-0"
                }`}
              />
              <div
                className={`absolute top-0 right-0 z-20 w-5 sm:w-8 lg:w-13.75 h-full bg-linear-to-l from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${
                  showRightGradient ? "opacity-100" : "opacity-0"
                }`}
              />
            </>
          )}
        </Swiper>
      </>
    );
  },
);

ProductColors.displayName = "ProductColors";

export default ProductColors;
